import { canonicalProductSchema, productInsertSchema, productRecordSchema, reviewAggregateSchema, reviewRecordSchema, sessionUserSchema, type ProductInsert } from "@lib/schema";

async function apiFetch<T>(url: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  const response = await fetch(url, {
    credentials: "include",
    ...init,
    headers
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed with ${response.status}`);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  const data = await response.json();
  return data as T;
}

export async function fetchSession() {
  const data = await apiFetch<{ user: unknown }>("/api/session");
  return data.user ? sessionUserSchema.parse(data.user) : null;
}

export interface FetchProductsOptions {
  includeAll?: boolean;
}

export async function fetchProducts(options: FetchProductsOptions = {}) {
  const query = options.includeAll ? "?all=1" : "";
  const data = await apiFetch<{ products: Array<any> }>(`/api/products${query}`);
  return data.products.map((item) => ({
    ...productRecordSchema.parse(item),
    summary: item.summary ?? null
  }));
}

export async function createProduct(payload: ProductInsert) {
  const parsed = productInsertSchema.parse(payload);
  const data = await apiFetch<{ product: unknown }>("/api/products", {
    method: "POST",
    body: JSON.stringify(parsed)
  });
  return productRecordSchema.parse(data.product);
}

export async function triggerEnrichment() {
  return apiFetch<{ processed: Array<{ productId: string; status: string }>; count: number }>("/api/admin/enrich", {
    method: "POST"
  });
}

export async function fetchProduct(slug: string) {
  const data = await apiFetch<{ product: unknown; canonical: unknown; citations: unknown }>(
    `/api/products/${slug}`
  );
  return {
    product: productRecordSchema.parse(data.product),
    canonical: canonicalProductSchema.parse(data.canonical),
    citations: Array.isArray(data.citations) ? data.citations : []
  };
}

export async function fetchReviews(productId: string) {
  const data = await apiFetch<{ reviews: unknown[]; aggregate: unknown }>(
    `/api/reviews?product=${productId}`
  );
  return {
    reviews: data.reviews.map((item) => reviewRecordSchema.parse(item)),
    aggregate: reviewAggregateSchema.parse(data.aggregate)
  };
}

export interface ReviewSubmission {
  product_id: string;
  rating: number;
  title: string;
  body: string;
  turnstileToken: string;
}

export async function submitReview(payload: ReviewSubmission) {
  await apiFetch("/api/reviews", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function logout() {
  await apiFetch("/api/logout", { method: "POST" });
}

export async function fetchCompare(ids: string) {
  const data = await apiFetch<{ result: unknown }>(`/api/compare?ids=${encodeURIComponent(ids)}`);
  return data.result as {
    rows: Array<{ key: string; left?: string; right?: string; status: string }>;
    prosOverlap: string[];
    consOverlap: string[];
  };
}
